import { Typography, Button } from '@mui/material';
import SkeletonRow from './SkeletonRow';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDateWithMoment } from '../../pages/apps/billing/utilities/helpers/helpers';

type InvoiceItem = {
	invoice_id: string;
	invoice_number: string;
	amount_paid: number;
	status: string;
	currency: string;
	invoice_created_date: string;
	invoice_pdf: string;
	plan_name: string;
};

interface DashboardInvoicesTableProps {
	invoices: InvoiceItem[];
	isLoading: boolean;
	isFetching?: boolean;
	limit?: number;
}

export default function DashboardInvoicesTable({
	invoices,
	isLoading,
	isFetching = false,
	limit = 5
}: DashboardInvoicesTableProps) {
	const showSkeleton = isLoading || isFetching;
	const navigate = useNavigate();

	// Limit the invoices to the specified number
	const limitedInvoices = invoices.slice(0, limit);

	const handleViewMore = () => {
		navigate('/apps/transactions');
	};

	return (
		<div>
			{!showSkeleton && limitedInvoices.length === 0 ? (
				<div className="bg-white rounded-lg shadow-sm p-12 text-center">
					<Typography
						variant="body1"
						className="text-gray-600"
					>
						No transactions found.
					</Typography>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow-sm overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className="bg-gray-50 border-b border-gray-200">
								<tr>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Invoice
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Plan
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Date
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Status
									</th>
									<th className="px-6 py-4 text-left font-semibold text-gray-800">
										Amount
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{showSkeleton
									? Array.from({ length: 5 }).map((_, i) => (
											<SkeletonRow key={i} columns={5} />
									  ))
									: limitedInvoices.map((invoice: InvoiceItem) => (
									<tr
										key={invoice.invoice_id}
										className="hover:bg-gray-50 transition-colors"
									>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-900"
											>
												{invoice.invoice_number}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-700 capitalize"
											>
												{invoice.plan_name}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-700"
											>
												{formatDateWithMoment(invoice.invoice_created_date)}
											</Typography>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<span
												className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
													invoice.status === 'paid'
														? 'bg-green-100 text-green-800'
														: 'bg-gray-100 text-gray-600'
												}`}
											>
												{invoice.status}
											</span>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<Typography
												variant="body2"
												className="text-gray-700"
											>
												{formatCurrency(
													invoice.currency || 'usd',
													invoice.amount_paid || 0
												)}
											</Typography>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					
					{/* View More Button */}
					<div className="px-6 py-4 border-t border-gray-200 flex justify-center">
						<Button
							variant="contained"
							onClick={handleViewMore}
							className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-2 rounded-lg"
							style={{
								backgroundColor: '#FFC107',
								color: '#000',
								fontWeight: '600'
							}}
						>
							View More
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}